import type { SkillOrigin } from "./types";

/**
 * Authoritative, display-only Skill identity persisted with one chat turn.
 *
 * Deliberately excludes descriptions, instructions, target/file metadata, and
 * evidence. Reconstruct snapshots through `createSkillTurnMetadata` so extra
 * runtime fields on resolver objects cannot leak into presentation storage.
 */
export interface SkillReferenceSnapshot {
  readonly id: string;
  readonly name: string;
  readonly slug: string;
  readonly origin: SkillOrigin;
}

export type SkillSelectionPath = "explicit" | "inferred";

export interface SkillTurnMetadata {
  readonly skills: readonly SkillReferenceSnapshot[];
  readonly selectionPath: SkillSelectionPath;
  readonly runContextId?: string;
  readonly policyVersion?: number;
  readonly skillCount: number;
  readonly effectiveSourceCount?: number;
  readonly effectiveFileCount?: number;
  readonly guidanceChars?: number;
}

export interface CreateSkillTurnMetadataInput {
  readonly skills: readonly SkillReferenceSnapshot[];
  readonly selectionPath: SkillSelectionPath;
  readonly runContextId?: string;
  readonly policyVersion?: number;
  readonly effectiveSourceCount?: number;
  readonly effectiveFileCount?: number;
  readonly guidanceChars?: number;
}

type UnknownRecord = Record<string, unknown>;

const SKILL_ORIGINS = new Set<SkillOrigin>(["personal", "organization", "finster"]);

function isUnknownRecord(value: unknown): value is UnknownRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function readBoundedString(value: unknown, maxLength: number): string | undefined {
  if (typeof value !== "string" || value.trim().length === 0 || value.length > maxLength) {
    return undefined;
  }
  return value;
}

function readSafeIdentifier(value: unknown): string | undefined {
  return typeof value === "string" && /^[A-Za-z0-9._:-]{1,256}$/.test(value) ? value : undefined;
}

function readOptionalCount(value: unknown): number | undefined {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0 ? value : undefined;
}

/**
 * Validates unknown persisted/event input and copies only display-safe fields.
 * Duplicate IDs use the first authoritative occurrence, preserving resolver
 * order while making deterministic event replay idempotent.
 */
export function normalizeSkillReferenceSnapshots(value: unknown): SkillReferenceSnapshot[] {
  if (!Array.isArray(value)) return [];

  const seenIds = new Set<string>();
  const snapshots: SkillReferenceSnapshot[] = [];
  for (const candidate of value) {
    if (!isUnknownRecord(candidate)) continue;
    const id = readBoundedString(candidate.id, 256);
    const name = readBoundedString(candidate.name, 256);
    const slug = readBoundedString(candidate.slug, 128);
    const origin = candidate.origin;
    if (
      !id ||
      !name ||
      !slug ||
      typeof origin !== "string" ||
      !SKILL_ORIGINS.has(origin as SkillOrigin) ||
      seenIds.has(id)
    ) {
      continue;
    }
    seenIds.add(id);
    snapshots.push({ id, name, slug, origin: origin as SkillOrigin });
  }
  return snapshots;
}

/**
 * Reads a legacy/local-storage value without trusting its TypeScript shape.
 * Invalid or empty metadata is ignored so older conversations keep rendering.
 */
export function readSkillTurnMetadata(value: unknown): SkillTurnMetadata | undefined {
  if (!isUnknownRecord(value)) return undefined;

  const skills = normalizeSkillReferenceSnapshots(value.skills);
  const selectionPath = value.selectionPath;
  if (skills.length === 0 || (selectionPath !== "explicit" && selectionPath !== "inferred")) {
    return undefined;
  }

  const runContextId = readSafeIdentifier(value.runContextId);
  const policyVersion = readOptionalCount(value.policyVersion);
  const effectiveSourceCount = readOptionalCount(value.effectiveSourceCount);
  const effectiveFileCount = readOptionalCount(value.effectiveFileCount);
  const guidanceChars = readOptionalCount(value.guidanceChars);

  return {
    skills,
    selectionPath,
    ...(runContextId ? { runContextId } : {}),
    ...(policyVersion !== undefined ? { policyVersion } : {}),
    skillCount: skills.length,
    ...(effectiveSourceCount !== undefined ? { effectiveSourceCount } : {}),
    ...(effectiveFileCount !== undefined ? { effectiveFileCount } : {}),
    ...(guidanceChars !== undefined ? { guidanceChars } : {}),
  };
}

export function createSkillTurnMetadata(input: CreateSkillTurnMetadataInput): SkillTurnMetadata {
  const metadata = readSkillTurnMetadata(input);
  if (!metadata) {
    throw new TypeError("Skill turn metadata requires valid ordered Skill references.");
  }
  return metadata;
}

/**
 * Display-only metadata for the synchronous user-message echo. Origin is
 * filled in when the resolver returns; chips only need id / name / slug to
 * paint, so a pending `finster` origin is safe until that patch lands.
 */
export function createPendingSkillTurnMetadata(
  skills: readonly Pick<SkillReferenceSnapshot, "id" | "name" | "slug">[]
): SkillTurnMetadata | undefined {
  if (skills.length === 0) return undefined;
  return createSkillTurnMetadata({
    skills: skills.map((skill) => ({
      id: skill.id,
      name: skill.name,
      slug: skill.slug,
      origin: "finster",
    })),
    selectionPath: "explicit",
  });
}
